import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Steps, Space, Button, Table, Tag, Checkbox, Upload, Input, message, Divider } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteBusinessFile, getDebtNoticeBatchStatus, sendDebtNotice, sendDebtNoticeCode, uploadBusinessFile } from '../../services/debt';
import { loadState, saveState } from './PublishNoticeEditContent/state';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';

/**
 * 催告发布步骤6：发送
 * 说明：该步骤后续将接入短信/邮件/微信等渠道发送，并落库发送结果。
 */
const PublishNoticeSendPage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const state = loadState();
  const { user } = useSelector((s) => s.auth || {});
  const [sendSms, setSendSms] = useState(!!state.sendSms);
  const [sendEmail, setSendEmail] = useState(!!state.sendEmail);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState(Array.isArray(state.noticeAttachments) ? state.noticeAttachments : []);
  const [batchStatus, setBatchStatus] = useState(null);

  useEffect(() => {
    if ((!state.selectedDebtId && !id) || !state.saved) {
      navigate('/debt-notice/publish');
    }
  }, [id, navigate, state.saved, state.selectedDebtId]);

  const prev = () => {
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/generate-pdf/${debtId}`);
  };

  const next = () => {
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/archive/${debtId}`);
  };

  const records = useMemo(() => (Array.isArray(state.generatedPdfRecords) ? state.generatedPdfRecords : []), [state.generatedPdfRecords]);

  const canSend = records.length > 0 && (sendSms || sendEmail);

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告函' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 5;

  const uploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const maxSize = 10 * 1024 * 1024;
      const name = String(file?.name || '').toLowerCase();
      const size = Number(file?.size || 0);
      const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.zip', '.7z'];
      const matched = allowedExts.some((ext) => name.endsWith(ext));
      if (!matched) {
        message.error('仅支持上传 PDF、图片或压缩文件');
        onError && onError(new Error('不支持的文件类型'));
        return;
      }
      if (size <= 0 || size > maxSize) {
        message.error('附件大小需在 0-10MB 之间');
        onError && onError(new Error('文件大小超出限制'));
        return;
      }
      try {
        const userId = user?.id;
        if (!userId) {
          message.error('未登录或用户信息缺失');
          onError && onError(new Error('用户未登录'));
          return;
        }
        const { data } = await uploadBusinessFile(file, 'notice_attachment_temp', userId);
        if (data?.objectKey) {
          const nextList = [{
            fileId: data.id,
            fileName: data.name,
            fileObjectKey: data.objectKey,
            previewUrl: data.url,
          }];
          setAttachments(nextList);
          saveState({ noticeAttachments: nextList });
          message.success('附件上传成功');
        }
        onSuccess && onSuccess(data);
      } catch (err) {
        message.error('附件上传失败');
        onError && onError(err);
      }
    },
  };

  const removeAttachment = async (idx) => {
    const userId = user?.id;
    const item = attachments[idx] || {};
    try {
      if (item?.fileObjectKey && userId) {
        await deleteBusinessFile(item.fileObjectKey, 'notice_attachment_temp', userId);
      }
    } catch (e) {
    }
    const nextList = [...attachments];
    nextList.splice(idx, 1);
    setAttachments(nextList);
    saveState({ noticeAttachments: nextList });
  };

  const handleSendCode = async () => {
    const debtId = id || state.selectedDebtId;
    if (!debtId) return;
    setSendingCode(true);
    try {
      await sendDebtNoticeCode(debtId);
      message.success('验证码已发送，请查收短信');
    } catch (e) {
      message.error(e?.message || '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  const refreshBatch = async (batchId) => {
    if (!batchId) return;
    try {
      const { data } = await getDebtNoticeBatchStatus(batchId);
      setBatchStatus(data);
      saveState({ noticeBatchStatus: data });
    } catch (e) {
    }
  };

  const handleSend = async () => {
    const debtId = id || state.selectedDebtId;
    if (!debtId) return;
    if (!canSend) {
      message.warning('请先选择发送方式，并确保已生成PDF');
      return;
    }
    if (!String(verificationCode || '').trim()) {
      message.warning('请输入短信验证码');
      return;
    }
    setSending(true);
    try {
      const payload = {
        sendSms,
        sendEmail,
        verificationCode: String(verificationCode || '').trim(),
        generatedPdfRecords: records,
        attachments: attachments.map((a) => ({
          fileId: a.fileId,
          fileName: a.fileName,
          fileObjectKey: a.fileObjectKey,
        })),
        contentHtml: state.contentHtml,
        pageSettings: state.pageSettings,
        placeholderFieldMap: state.placeholderFieldMap,
        placeholderManualMap: state.placeholderManualMap,
        selectedDebtorKeys: state.selectedDebtorKeys,
        signature: {
          applySignature: !!state.applySignature,
          sealId: state.sealId || null,
          sealWidthMm: Number(state.sealWidthMm || 30),
          sealAspect: Number(state.sealAspect || 1),
          sealOffsetRightMm: Number.isFinite(Number(state.sealOffsetRightMm)) ? Number(state.sealOffsetRightMm) : 8,
          sealOffsetBottomMm: Number.isFinite(Number(state.sealOffsetBottomMm)) ? Number(state.sealOffsetBottomMm) : 8,
        },
      };
      const { data } = await sendDebtNotice(debtId, payload);
      setBatchStatus(data);
      saveState({ noticeBatchStatus: data, noticeLastBatchId: data?.batchId });
      message.success('已发起发送');
    } catch (e) {
      message.error(e?.message || '发送失败');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <h1 className="page-title">发布催告</h1>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
                <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
              </div>
              <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
            </div>
          ) : (
            <Steps current={currentStep} size="small" items={steps} />
          )}
        </Card>

        <div className="mb-md">
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            style={{ width: isMobile ? '100%' : undefined }}
          >
            <Button onClick={prev} block={isMobile}>上一步</Button>
            <Button type="primary" onClick={next} block={isMobile}>下一步</Button>
          </Space>
        </div>

        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Space wrap>
            <Checkbox checked={sendSms} onChange={(e) => { setSendSms(e.target.checked); saveState({ sendSms: e.target.checked }); }}>发送短信</Checkbox>
            <Checkbox checked={sendEmail} onChange={(e) => { setSendEmail(e.target.checked); saveState({ sendEmail: e.target.checked }); }}>发送邮件</Checkbox>
            <Tag color={records.length ? 'green' : 'default'}>{`收件人 ${records.length} 个`}</Tag>
            <Tag color={records.length ? 'geekblue' : 'default'}>{`催告函 ${records.length} 份`}</Tag>
            <Tag color={attachments.length ? 'blue' : 'default'}>{`附件 ${attachments.length} 个`}</Tag>
          </Space>

          <Divider style={{ margin: '8px 0' }} />

          <Space wrap>
            <Upload {...uploadProps} disabled={attachments.length >= 1}>
              <Button type="primary" disabled={attachments.length >= 1}>上传附件</Button>
            </Upload>
            <span style={{ color: '#666' }}>催告函以外的其他附件（不超过10M的PDF，图片及压缩文件）</span>
          </Space>

          {attachments.length ? (
            <Table
              size="small"
              rowKey={(r, idx) => `${r?.fileObjectKey || ''}-${idx}`}
              dataSource={attachments}
              pagination={false}
              columns={[
                { title: '文件名', dataIndex: 'fileName', key: 'fileName', render: (v) => v || '-' },
                {
                  title: '操作',
                  key: 'op',
                  width: 180,
                  render: (_, row, idx) => (
                    <Space>
                      <Button size="small" onClick={() => row?.previewUrl && window.open(row.previewUrl, '_blank', 'noopener,noreferrer')} disabled={!row?.previewUrl}>预览</Button>
                      <Button size="small" danger onClick={() => removeAttachment(idx)}>移除</Button>
                    </Space>
                  ),
                },
              ]}
            />
          ) : null}

          <Divider style={{ margin: '8px 0' }} />

          <Space wrap>
            <Button onClick={handleSendCode} loading={sendingCode} disabled={!user?.id}>获取短信验证码</Button>
            <Input
              style={{ width: 120 }}
              placeholder="请输入短信验证码"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <Button type="primary" onClick={handleSend} loading={sending} disabled={!canSend}>确认发送</Button>
          </Space>

          <Divider style={{ margin: '8px 0' }} />

          {batchStatus?.batchId ? (
            <Card size="small" title="发送结果" extra={<Button size="small" onClick={() => refreshBatch(batchStatus.batchId)}>刷新</Button>}>
              <Space wrap style={{ marginBottom: 8 }}>
                <Tag color="blue">{`批次ID ${batchStatus.batchId}`}</Tag>
                <Tag color={batchStatus.status === 'SUCCESS' ? 'green' : (batchStatus.status === 'FAILED' ? 'red' : 'orange')}>{batchStatus.status || '-'}</Tag>
              </Space>
              <Table
                size="small"
                rowKey={(r) => String(r?.id)}
                dataSource={Array.isArray(batchStatus?.tasks) ? batchStatus.tasks : []}
                pagination={{
                  pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                  showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
                  pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                }}
                columns={[
                  { title: '债务人', dataIndex: 'debtorName', key: 'debtorName', width: 200, render: (v) => v || '-' },
                  {
                    title: '渠道',
                    dataIndex: 'channel',
                    key: 'channel',
                    width: 90,
                    render: (v) => {
                      if (v === 'SMS') return '短信';
                      if (v === 'EMAIL') return '邮件';
                      return v || '-';
                    },
                  },
                  { title: '发送目标', dataIndex: 'sendTo', key: 'sendTo', width: 220, render: (v) => v || '-' },
                  { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => v || '-' },
                  { title: '错误', dataIndex: 'errorMessage', key: 'errorMessage', render: (v) => v || '-' },
                ]}
              />
            </Card>
          ) : null}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default PublishNoticeSendPage;
